import { Box, Divider, ThemeProvider, useMediaQuery, Tabs, Tab } from '@mui/material';
import { ChatBubbleOutline, AutoFixHigh } from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import theme from '../../theme';

import { ChatMessage, generateDiagramXML, getDiagramStatus } from './IdeationService';

import { RootState } from '../../store';
import ShowToast from '../../common/components/ShowToast';
import { ToastTypeEnum } from '../../constants/solutionXPLR';
import { NewIdeationState, updateIdeationFieldState } from '../NewIdeationXPLR/state';
import { compareVersions } from '../../common/utils';
import ChatListingSidebar from './components/chatListing/ChatListingSidebar';

import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CoiChat from './components/chatSession/CoiChat';
import CoiUseCase from './components/semanticKnowledge/CoiUseCase';
import ProcessCreation from './components/chatLandingPage/ProcessCreation';

// Import newly created hooks and utils
import { useVersionSync } from './hooks/useVersionSync';
import { useDataXPLRSessions } from './hooks/useDataXPLRSessions';
import { useChatMessaging } from './hooks/useChatMessaging';
import { useDiagramStatusPolling } from './hooks/useDiagramStatusPolling';
import {
  categoryFieldMap,
  getFieldTypeFromCategory,
  getFieldKeyFromAPIType,
  getCategoryFromField,
  getAPIKeyFromField,
} from './utils';

import {
  chatBotFieldMap,
  fieldAPIKeyMap,
  fieldLabelMap,
} from '../NewIdeationXPLR/shared/constants';
import { OpportunityDetailFieldKey } from '../NewIdeationXPLR/shared/types';
import { ENVIRONMENT_TYPE } from '../../config/env';

interface Process {
  id: string;
  name: string;
  date: string;
  subtitle?: string;
  context_knowledge?: Record<string, string>;
}

interface UploadedFile {
  id: string;
  name: string;
  file: File;
  url?: string;
  isUploading?: boolean;
  isAnalyzing?: boolean;
  fileData?: string;
  fileSize?: string;
  finalSummary?: string;
  extractedData?: any[];
  filePath?: string;
}

interface ProcessCreationState {
  details: string;
  uploadedFiles: UploadedFile[];
  selectedContexts: string[];
  recentProcesses: Process[];
  isLoadingRecents: boolean;
  contextProcesses: Process[];
  isLoadingContext: boolean;
  fileSummary: string;
}

interface IdeationChatbotProps {
  setActiveStep?: (step: number) => void;
  field?: OpportunityDetailFieldKey;
  handleBack?: () => void;
}

const DataXPLR: React.FC<IdeationChatbotProps> = ({
  field: defaultField = 'currentProcess',
  handleBack = () => {},
}) => {
  const { id: initialChatId = '' } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const organization_id = localStorage.getItem('current_organization') || '';

  // State retained in main component
  const [chatWidth, setChatWidth] = useState<number>(600);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [useCaseDetails, setUseCaseDetails] = useState<Record<string, any>>({});
  const [contextState, setContextState] = useState<ProcessCreationState>({
    details: '',
    uploadedFiles: [],
    selectedContexts: [],
    recentProcesses: [],
    isLoadingRecents: true,
    contextProcesses: [],
    isLoadingContext: false,
    fileSummary: '',
  });
  const [allContextData, setAllContextData] = useState<any[]>([]);
  const [, setIsUseDataXplrModalOpen] = useState(false);
  const [isSendingToSolutionXPLR, setIsSendingToSolutionXPLR] = useState(false);
  const [editorContent, setEditorContent] = useState<string>('');
  const [chatId, setChatId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('searchQuery') || '');
  const [showKnowledgeCreation, setShowKnowledgeCreation] = useState(!initialChatId);
  const [activeSessionFieldType, setActiveSessionFieldType] = useState<string>(
    location.state?.sessionFieldType || ''
  );
  const rawSelectedCategoryParam = useMemo(
    () => searchParams.get('selectedCategory') || '',
    [searchParams]
  );

  const openInEditMode = useMemo(() => searchParams.get('edit') === 'true', [searchParams]);

  /**
   * Canonical backend field_type for the current view.
   * The URL may contain one of:
   * - backend field_type (e.g. current_process, ..._solutionxplr)
   * - component field key (e.g. currentProcess)
   * - readable label (e.g. "Current Process (Solution)")
   */
  const currentFieldType = useMemo(() => {
    const raw =
      rawSelectedCategoryParam && rawSelectedCategoryParam !== 'All'
        ? rawSelectedCategoryParam
        : defaultField;

    return (
      fieldAPIKeyMap[raw as keyof typeof fieldAPIKeyMap] ||
      getFieldTypeFromCategory(raw) ||
      getAPIKeyFromField(raw) ||
      raw
    );
  }, [rawSelectedCategoryParam, defaultField]);

  const resolvedSessionFieldType = useMemo(() => {
    return activeSessionFieldType || currentFieldType;
  }, [activeSessionFieldType, currentFieldType]);
  /** Component field key used for ideation state mapping. */
  const currentField = useMemo(() => getFieldKeyFromAPIType(currentFieldType), [currentFieldType]);

  /** Readable label used for sidebar filtering & UI. */
  const selectedCategory = useMemo(() => {
    if (!rawSelectedCategoryParam) return 'All';
    return (
      categoryFieldMap(getCategoryFromField(currentFieldType) || rawSelectedCategoryParam) || 'All'
    );
  }, [rawSelectedCategoryParam, currentFieldType]);
  const [sortBy, setSortBy] = useState('newest_first');
  const [selectedSessionName, setSelectedSessionName] = useState<string>('');
  const [, setSelectedSessionFieldType] = useState<string>(location.state?.sessionFieldType || '');
  const [, setSelectedSessionConversationType] = useState<string>(
    location.state?.conversationType || ''
  );
  const [failedMessageData, setFailedMessageData] = useState<any>(null);
  const skipChatLoadRef = useRef(false);
  const hasCleanedEditParamRef = useRef(false);
  const initialLoadRef = useRef(false);

  const { recent_imported_chat } = useSelector((state: RootState) => state.newIdeation);

  // Hook: Version Sync
  const {
    currentVersion,
    setCurrentVersion,
    versionData,
    fileSummary,
    handleVersionChange,
    //createVersionData,
  } = useVersionSync(chatId, messages);

  // Hook: DataXPLR Sessions management
  const {
    chatSessions,
    setChatSessions,
    elasticChatSessions,
    setSelectedElasticChat,
    isLoadingMore,
    hasReachedEnd,
    loadSessions,
    //loadElasticChatSessions,
    handleLoadMore,
    addOrUpdateSession,
    isInitialLoading,
  } = useDataXPLRSessions(organization_id, searchQuery, selectedCategory, sortBy);

  // Hook: Chat Messaging
  const {
    isSending,
    reportLoading,
    setUploadingImage,
    handleSendMessage,
    getPreviousChat,
    pollingTimeoutRef,
    diagramXml,
    setDiagramXml,
    isDiagramLoading,
    setIsDiagramLoading,
    diagramStatus,
    setDiagramStatus,
    conversationType,
  } = useChatMessaging({
    chatId,
    setChatId,
    currentField,
    currentFieldType: resolvedSessionFieldType,
    //selectedSessionFieldType,
    //selectedSessionConversationType,
    //chatSessions,
    inputMessage,
    setInputMessage,
    organization_id,
    navigate,
    useCaseDetails,
    setUseCaseDetails,
    contextState,
    setContextState,
    allContextData,
    setAllContextData,
    versionData,
    loadSessions,
    selectedCategory,
    selectedSessionName,
    messages,
    setMessages,
    onNewSession: addOrUpdateSession,
  });

  const [enableDiagramPolling, setEnableDiagramPolling] = useState(false);
  const [diagramVersion, setDiagramVersion] = useState<string>('');

  useDiagramStatusPolling({
    conversationId: chatId,
    organizationId: organization_id,
    conversationType: conversationType,
    fieldType: fieldAPIKeyMap[currentField],
    enabled: enableDiagramPolling,
    onStatusUpdate: (status) => {
      setDiagramStatus(status);
    },
    onSuccess: (xml, diagram_version) => {
      setDiagramXml(xml);
      setDiagramVersion(diagram_version || '');
      setEnableDiagramPolling(false);
      setIsDiagramLoading(false);
      ShowToast({
        message: 'Diagram Generated Successfully',
        type: ToastTypeEnum.Success,
      });
    },
    onError: (error) => {
      setEnableDiagramPolling(false);
      setIsDiagramLoading(false);
      ShowToast({
        message: error,
        type: ToastTypeEnum.Error,
      });
    },
  });

  // Auto-polling on session load if diagram is in progress
  useEffect(() => {
    if (!chatId || diagramXml || !organization_id || enableDiagramPolling) return;

    const checkStatusOnLoad = async () => {
      try {
        const response = await getDiagramStatus({
          conversation_id: chatId,
          organization_id: organization_id,
          conversation_type: conversationType,
          field_type: fieldAPIKeyMap[currentField],
        });

        const statusData = response?.data;
        if (
          statusData &&
          (statusData.is_diagram_status === 'in_progress' ||
            statusData.is_diagram_status === 'Processing')
        ) {
          setDiagramStatus(statusData.is_diagram_status);
          setIsDiagramLoading(true);
          setEnableDiagramPolling(true);
        } else if (
          statusData &&
          (statusData.is_diagram_status === 'completed' ||
            statusData.is_diagram_status === 'Completed')
        ) {
          setDiagramStatus('completed');
          if (statusData.diagramContent) {
            setDiagramXml(statusData.diagramContent);
            setDiagramVersion(statusData.diagramVersion);
          }
        } else if (
          statusData &&
          (statusData.is_diagram_status === 'error' || statusData.is_diagram_status === 'failed')
        ) {
          setDiagramStatus('error');
          setIsDiagramLoading(false);
          setEnableDiagramPolling(false);
          ShowToast({
            message: 'Failed to generate diagram',
            type: ToastTypeEnum.Error,
          });
        }
      } catch (err) {
        console.error('Error checking diagram status on load:', err);
      }
    };

    checkStatusOnLoad();
  }, [chatId, diagramXml, organization_id, conversationType, currentField, enableDiagramPolling]);

  // Re-sync version hook with messages
  // (Internal implementation of useVersionSync uses messages,
  // but we need to ensure it sees the latest messages from useChatMessaging)
  // Re-sync version hook with messages moved to useVersionSync internal logic

  // Version sync with Redux handled in useVersionSync

  const handleChatWidthChange = (width: number) => {
    setChatWidth(width);
  };

  const handleSendUseCaseData = async () => {
    const versions = Object.keys(versionData);
    const latestVersionNumber = versions?.sort(compareVersions).at(-1);
    if (isSendingToSolutionXPLR) return;
    setIsSendingToSolutionXPLR(true);
    try {
      dispatch(
        updateIdeationFieldState({
          field: currentField as keyof NewIdeationState,
          value: editorContent,
        })
      );
      const chatDetails: Record<string, any> = {
        ...recent_imported_chat,
        [chatBotFieldMap[currentField]]: {
          chat_id: chatId,
          version_id: latestVersionNumber,
        },
      };
      dispatch(
        updateIdeationFieldState({
          field: 'recent_imported_chat',
          value: chatDetails,
        })
      );
    } catch (error) {
      console.error('Error sending to Solution XPLR:', error);
      ShowToast({
        message: 'Failed to send to Solution XPLR',
        type: ToastTypeEnum.Error,
      });
    } finally {
      setIsSendingToSolutionXPLR(false);
    }
  };

  const resetData = (showDashboard: boolean = false) => {
    setMessages([]);
    setChatId('');
    setInputMessage('');
    setUseCaseDetails({});
    setShowKnowledgeCreation(showDashboard);
    setActiveSessionFieldType('');
    setContextState({
      details: '',
      uploadedFiles: [],
      selectedContexts: [],
      recentProcesses: [],
      isLoadingRecents: true,
      contextProcesses: [],
      isLoadingContext: false,
      fileSummary: '',
    });
    setAllContextData([]);
    setDiagramXml('');
    setIsDiagramLoading(false);
    setEnableDiagramPolling(false);
    setDiagramStatus('');
    setDiagramVersion('');
  };

  const handleNewChat = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    resetData(true);
    navigate('/data-xplr');
  };

  const handleNewIdeation = () => {
    setShowKnowledgeCreation(true);
    setChatId('');
  };

  const updateChats = () => {
    getPreviousChat(chatId);
  };

  const handleBackClick = () => {
    const versions = Object.keys(versionData);
    const latestVersionNumber = versions?.sort(compareVersions).at(-1);

    const currFieldData = recent_imported_chat?.[chatBotFieldMap?.[currentField]];
    const hasLatestData = currFieldData?.chat_id
      ? currFieldData?.version_id === latestVersionNumber
      : false;
    if (
      versionData &&
      Object.keys(versionData)?.length > 0 &&
      (!currFieldData?.chat_id || !hasLatestData)
    ) {
      setIsUseDataXplrModalOpen(true);
    } else {
      handleBack?.();
    }
  };

  const handleFieldChange = (apiField: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('selectedCategory', String(apiField));
    navigate(`${location.pathname}?${params.toString()}`, {
      state: location.state, // ← back to original — safe now
    });
  };

  const handleSortChange = async (newSortBy: string) => {
    setSortBy(newSortBy);
  };

  const handleCategoryChange = (category: string) => {
    const params = new URLSearchParams(searchParams);
    if (category === 'All') {
      params.delete('selectedCategory');
    } else {
      const fieldType = getFieldTypeFromCategory(category) || category;
      params.set('selectedCategory', String(fieldType));
    }
    navigate(`${location.pathname}?${params.toString()}`, {
      state: location.state, // ← back to original — safe now
    });
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleSelectChat = (
    selectedChatId: string,
    sessionFieldType?: string,
    sessionName?: string,
    conversationType?: string
  ) => {
    if (selectedChatId === chatId) return;

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    setShowKnowledgeCreation(false);
    resetData();
    setChatId(selectedChatId);
    skipChatLoadRef.current = false;

    setSelectedSessionName(sessionName || '');
    setSelectedSessionFieldType(sessionFieldType || '');
    setSelectedSessionConversationType(conversationType || '');
    setActiveSessionFieldType(sessionFieldType || ''); // ← ADD THIS

    const fieldTypeParam = sessionFieldType || currentFieldType || '';
    const categoryParam =
      fieldTypeParam && selectedCategory !== 'All'
        ? `selectedCategory=${encodeURIComponent(fieldTypeParam)}&`
        : '';
    navigate(`/data-xplr/${selectedChatId}?${categoryParam}searchQuery=${searchQuery}`, {
      state: { skipChat: true, sessionFieldType, conversationType, sessionName },
    });
  };

  const generateDiagramXml = async (): Promise<void> => {
    try {
      setIsDiagramLoading(true);
      const versions = Object.keys(versionData);
      const latestVersionNumber = versions?.sort(compareVersions).at(-1) ?? '';

      const payload = {
        semanticKnowledge: editorContent || '',
        xml: diagramXml || '',
        field_type: fieldAPIKeyMap[currentField],
        conversation_id: chatId,
        organization_id: organization_id,
        conversation_type: conversationType,
        env_type: ENVIRONMENT_TYPE,
        label: `${chatId}_${fieldAPIKeyMap[currentField]}_${conversationType}`,
        diagram_version: latestVersionNumber,
      };

      generateDiagramXML(payload)
        .then((diagramData) => {
          if (diagramData && diagramData?.diagramContent) {
            setDiagramXml(diagramData?.diagramContent);
            setDiagramVersion(diagramData?.diagramVersion ?? latestVersionNumber);
            setDiagramStatus('completed');
            setEnableDiagramPolling(false);
            setIsDiagramLoading(false);
            ShowToast({ message: 'Diagram Generated Successfully', type: ToastTypeEnum.Success });
          }
        })
        .catch((err) => {
          console.error('Orchestrator error:', err);
        });

      setEnableDiagramPolling(true);
    } catch (err) {
      console.error(err);
      setIsDiagramLoading(false);
      ShowToast({
        message: 'Failed to trigger diagram generation. Please try again',
        type: ToastTypeEnum.Error,
      });
    }
  };

  // Consolidate URL and state cleanup into a single effect to minimize redundant navigations
  useEffect(() => {
    const hasDrawerState = location.state?.fromDrawer && location.state?.selectedCategory;
    const hasEditParam = searchParams.get('edit') === 'true';

    if (
      (hasDrawerState || (hasEditParam && chatId && !hasCleanedEditParamRef.current)) &&
      !initialLoadRef.current
    ) {
      const params = new URLSearchParams(searchParams);
      if (hasEditParam) {
        params.delete('edit');
        hasCleanedEditParamRef.current = true;
      }

      const newSearch = params.toString();
      const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

      navigate(newPath, {
        replace: true,
        state: {
          ...location.state,
          fromDrawer: false, // Clear the cleanup triggers
          edit: undefined,
        },
      });
    }
  }, [location.state, location.pathname, location.search, searchParams, navigate, chatId]);

  // loadSessions and loadElasticChatSessions are handled internally by useDataXPLRSessions

  useEffect(() => {
    if (!initialChatId) return;
    initialLoadRef.current = true;
    setShowKnowledgeCreation(false);
    setChatId(initialChatId);
    // Restore session field type from location state on direct URL access
    if (location.state?.sessionFieldType) {
      setActiveSessionFieldType(location.state.sessionFieldType);
    }
    getPreviousChat(initialChatId);
    setCurrentVersion('');
  }, [initialChatId]); // Removed currentFieldType to prevent redundant calls on sidebar filter change

  // Re-sync version hook with messages moved to useVersionSync internal logic

  useEffect(() => {
    if (messages.length > 0 && failedMessageData) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.status === 'completed') {
        setFailedMessageData(null);
      }
    }
  }, [messages, failedMessageData]);

  // ── MOBILE additions ─────────────────────────────────────────────────────
  const isMobile = useMediaQuery('(max-width:900px)');
  const [mobileTab, setMobileTab] = useState(0); // 0 = Chat, 1 = Semantic Knowledge
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          height: '93vh',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
            gap: 0,
            flex: 1,
            minHeight: 0,
          }}
        >
          <ChatListingSidebar
            sessions={chatSessions}
            currentChatId={chatId}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
            onNewIdeation={handleNewIdeation}
            onCategoryChange={handleCategoryChange}
            onSearchChange={handleSearchChange}
            onRefetch={() => loadSessions(getFieldTypeFromCategory(selectedCategory))}
            selectedCategory={selectedCategory}
            setuseCaseDetails={setUseCaseDetails}
            useCaseDetails={useCaseDetails}
            onLoadMore={handleLoadMore}
            isLoadingMore={isLoadingMore}
            hasReachedEnd={hasReachedEnd}
            searchFromQuery={searchQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onSortChange={handleSortChange}
            elasticChatSessions={elasticChatSessions}
            setSelectedElasticChat={setSelectedElasticChat}
            setSessions={setChatSessions}
            isInitialLoading={isInitialLoading}
          />
          {showKnowledgeCreation ? (
            <ProcessCreation
              field={currentField}
              handleForward={(id: string) => {
                const fieldType =
                  getFieldTypeFromCategory(selectedCategory) ?? getAPIKeyFromField(currentField);
                addOrUpdateSession({
                  id,
                  conversation_id: id,
                  use_case_title: 'Generating...',
                  session_name: '',
                  created_by: 'me',
                  updated_by: '',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  usedInProcess: fieldLabelMap[currentField] ?? currentField,
                  field_type: fieldType,
                  ideation_intake: [],
                });
                // setMobileTab(0);
                navigate(`/data-xplr/${id}?selectedCategory=${fieldType}`, {
                  state: { isNewChat: true, sessionFieldType: fieldType },
                });
              }}
              sessions={chatSessions}
              currentChatId={chatId}
              onSelectChat={handleSelectChat}
              onNewChat={handleNewChat}
              onNewIdeation={handleNewIdeation}
              onFieldChange={handleFieldChange}
            />
          ) : (
            <>
              {/* ── MOBILE: tab bar + panels ── */}
              {isMobile ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Tabs
                    value={mobileTab}
                    onChange={(_, v) => setMobileTab(v)}
                    variant="fullWidth"
                    sx={{
                      borderBottom: '1px solid #EBEBEB',
                      backgroundColor: '#ffffff',
                      minHeight: '48px',
                      p: '4px',
                      '& .MuiTabs-indicator': { display: 'none' },
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        minHeight: '40px',
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                      },
                    }}
                  >
                    <Tab
                      icon={<ChatBubbleOutline sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                      label="Chat"
                      sx={{
                        color: mobileTab === 0 ? '#ffffff !important' : '#737373',
                        backgroundColor: mobileTab === 0 ? '#1A55F9' : 'transparent',
                      }}
                    />
                    <Tab
                      icon={<AutoFixHigh sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                      label="Semantic Knowledge"
                      sx={{
                        color: mobileTab === 1 ? '#ffffff !important' : '#737373',
                        backgroundColor: mobileTab === 1 ? '#1A55F9' : 'transparent',
                      }}
                    />
                  </Tabs>
                  <Box
                    sx={{
                      display: mobileTab === 0 ? 'flex' : 'none',
                      flex: 1,
                      minHeight: 0,
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <CoiChat
                      width={chatWidth}
                      onWidthChange={handleChatWidthChange}
                      onVersionChange={handleVersionChange}
                      currentVersion={currentVersion}
                      handleSendMessage={handleSendMessage}
                      messages={messages}
                      setMessages={setMessages}
                      isSending={isSending[chatId] || false}
                      inputMessage={inputMessage}
                      setInputMessage={setInputMessage}
                      useCaseDetails={useCaseDetails}
                      versionData={versionData}
                      handleBack={handleBackClick}
                      field={activeSessionFieldType || currentField}
                      setContextState={setContextState}
                      contextState={contextState}
                      setChatId={setChatId}
                      initialChatId={initialChatId}
                      chatId={chatId}
                      setuseCaseDetails={setUseCaseDetails}
                      onRefetchSessions={() =>
                        loadSessions(
                          getFieldTypeFromCategory(selectedCategory),
                          undefined,
                          false,
                          undefined,
                          selectedCategory
                        )
                      }
                      handleNewChat={() => resetData(true)}
                      setUploadingImage={setUploadingImage}
                      conversationType={conversationType}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: mobileTab === 1 ? 'flex' : 'none',
                      flex: 1,
                      minHeight: 0,
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <CoiUseCase
                      key={`coi-usecase-${chatId || 'new'}`}
                      useCaseId={chatId}
                      selectedVersion={currentVersion}
                      onVersionChange={handleVersionChange}
                      versionData={versionData}
                      useCaseDetails={useCaseDetails}
                      updateChats={updateChats}
                      isSending={isSending[chatId] || false}
                      field={activeSessionFieldType || currentField}
                      setActiveStep={(_step: string) => {}}
                      fileSummary={fileSummary}
                      editorContent={editorContent}
                      setEditorContent={setEditorContent}
                      isSendingToSolutionXPLR={isSendingToSolutionXPLR}
                      setIsSendingToSolutionXPLR={setIsSendingToSolutionXPLR}
                      handleSendUseCaseData={handleSendUseCaseData}
                      reportLoading={reportLoading}
                      generateDiagramXml={generateDiagramXml}
                      diagramXml={diagramXml}
                      isDiagramLoading={isDiagramLoading}
                      diagramStatus={diagramStatus}
                      initialEditMode={openInEditMode}
                    />
                  </Box>
                </Box>
              ) : (
                /* ── DESKTOP: original layout ── */
                <>
                  <CoiChat
                    width={chatWidth}
                    onWidthChange={handleChatWidthChange}
                    onVersionChange={handleVersionChange}
                    currentVersion={currentVersion}
                    handleSendMessage={handleSendMessage}
                    messages={messages}
                    setMessages={setMessages}
                    isSending={isSending[chatId] || false}
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    useCaseDetails={useCaseDetails}
                    versionData={versionData}
                    handleBack={handleBackClick}
                    field={activeSessionFieldType || currentField}
                    setContextState={setContextState}
                    contextState={contextState}
                    setChatId={setChatId}
                    initialChatId={initialChatId}
                    chatId={chatId}
                    setuseCaseDetails={setUseCaseDetails}
                    onRefetchSessions={() =>
                      loadSessions(
                        getFieldTypeFromCategory(selectedCategory),
                        undefined,
                        false,
                        undefined,
                        selectedCategory
                      )
                    }
                    handleNewChat={() => resetData(true)}
                    setUploadingImage={setUploadingImage}
                    conversationType={conversationType}
                  />
                  <Divider orientation="vertical" sx={{ border: '3px solid #EBEBEB' }} />
                  <CoiUseCase
                    key={`coi-usecase-${chatId || 'new'}`}
                    useCaseId={chatId}
                    selectedVersion={currentVersion}
                    onVersionChange={handleVersionChange}
                    versionData={versionData}
                    useCaseDetails={useCaseDetails}
                    updateChats={updateChats}
                    isSending={isSending[chatId] || false}
                    field={activeSessionFieldType || currentField}
                    setActiveStep={(_step: string) => {}}
                    fileSummary={fileSummary}
                    editorContent={editorContent}
                    setEditorContent={setEditorContent}
                    isSendingToSolutionXPLR={isSendingToSolutionXPLR}
                    setIsSendingToSolutionXPLR={setIsSendingToSolutionXPLR}
                    handleSendUseCaseData={handleSendUseCaseData}
                    reportLoading={reportLoading}
                    generateDiagramXml={generateDiagramXml}
                    diagramXml={diagramXml}
                    isDiagramLoading={isDiagramLoading}
                    diagramStatus={diagramStatus}
                    initialEditMode={openInEditMode}
                    diagramVersion={diagramVersion}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DataXPLR;
